import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { userService } from '../../services/userService';
import { roleService } from '../../services/roleService';
import UserForm from './UserForm';
import UserDetail from './UserDetail';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData] = await Promise.all([
        userService.getAllUsers(),
        roleService.getAllRoles(),
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      setError('');
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setOpenForm(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setOpenForm(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setOpenDetail(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(userId);
        setUsers(users.filter(u => u.id !== userId));
        setSuccess('User deleted successfully');
        setTimeout(() => setSuccess(''), 5000);
      } catch (err) {
        setError('Failed to delete user. Please try again.');
        console.error('Error deleting user:', err);
      }
    }
  };

  const handleFormSubmit = async (userData) => {
    try {
      if (selectedUser) {
        const updatedUser = await userService.updateUser(selectedUser.id, userData);
        setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
        setSuccess('User updated successfully');
      } else {
        const newUser = await userService.createUser(userData);
        setUsers([...users, newUser]);
        setSuccess('User created successfully');
      }
      setOpenForm(false);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('Failed to save user. Please try again.');
      console.error('Error saving user:', err);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.preferredName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = !filterRole || 
      user.roleIds?.some(roleId => 
        roles.find(role => role.id === roleId)?.name === filterRole
      );
    
    return matchesSearch && matchesRole;
  });

  const getRoleNames = (roleIds) => {
    if (!roleIds || !roles.length) return [];
    return roleIds
      .map(roleId => roles.find(role => role.id === roleId)?.name)
      .filter(Boolean);
  };

  const getDisplayName = (user) => {
    const name = user.preferredName || user.firstName;
    const lastNameInitial = user.lastName ? user.lastName.substring(0, 1) : '';
    const graduationYear = user.graduationYear || '';
    return `${name} ${lastNameInitial}. ${graduationYear}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Users Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
          sx={{ minWidth: 120 }}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search and Filter Card - Moved to top */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Users"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Filter by Role</InputLabel>
                <Select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  label="Filter by Role"
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.name}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>School Info</TableCell>
                <TableCell>Households</TableCell>
                <TableCell>Household Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {getDisplayName(user)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.firstName} {user.lastName}
                      {user.preferredName && user.preferredName !== user.firstName && (
                        <span> (Prefers: {user.preferredName})</span>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {getRoleNames(user.roleIds).map((roleName) => (
                        <Chip
                          key={roleName}
                          label={roleName}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      {user.school && (
                        <Typography variant="body2" color="text.secondary">
                          {user.school}
                        </Typography>
                      )}
                      {user.graduationYear && (
                        <Typography variant="body2" color="text.secondary">
                          Class of {user.graduationYear}
                        </Typography>
                      )}
                      {!user.school && !user.graduationYear && (
                        <Typography variant="body2" color="text.secondary">
                          No school info
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.householdIds ? user.householdIds.length : 0} household(s)
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {user.householdCodes && user.householdCodes.length > 0 ? (
                        user.householdCodes.map((code, index) => (
                          <Chip
                            key={index}
                            label={code}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No codes
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isActive ? 'Active' : 'Inactive'}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1} justifyContent="center">
                      <IconButton
                        size="small"
                        onClick={() => handleViewUser(user)}
                        color="primary"
                        title="View Details"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        color="primary"
                        title="Edit User"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteUser(user.id)}
                        color="error"
                        title="Delete User"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {filteredUsers.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No users found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || filterRole ? 'Try adjusting your search criteria.' : 'Create your first user to get started.'}
          </Typography>
        </Box>
      )}


      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#009494',
            color: 'white',
            fontWeight: 600,
            fontSize: '1.25rem',
            borderBottom: '2px solid #004343',
          }}
        >
          {selectedUser ? 'Edit User' : 'Create New User'}
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          <Box sx={{ p: 3 }}>
            <UserForm
              user={selectedUser}
              roles={roles}
              onSubmit={handleFormSubmit}
              onCancel={() => setOpenForm(false)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#009494',
            color: 'white',
            fontWeight: 600,
            fontSize: '1.25rem',
            borderBottom: '2px solid #004343',
          }}
        >
          User Details
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          <Box sx={{ p: 3 }}>
            {selectedUser && (
              <UserDetail user={selectedUser} roles={roles} />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Button 
            onClick={() => setOpenDetail(false)}
            variant="contained"
            sx={{
              backgroundColor: '#009494',
              '&:hover': {
                backgroundColor: '#004343',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users; 